import i18next from "i18next";

const LOCALE_MAP: Record<string, string> = {
  en: "en-US",
  vi: "vi-VN",
};

function getLocale(): string {
  return LOCALE_MAP[i18next.language] ?? "en-US";
}

/**
 * Format a date value as a short date string (no time) using the current app locale.
 * Returns '' for null/undefined/invalid input.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

/**
 * Format a date value as a date + time string using the current app locale.
 * Returns '' for null/undefined/invalid input.
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * Format a number using the current app locale.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(getLocale(), options).format(value);
}

/**
 * Format a byte count as a human-readable file size using the current app locale.
 * Locale affects the decimal separator (e.g. "1.5 KB" in en-US, "1,5 KB" in vi-VN).
 * Returns '' for null/undefined/NaN input.
 */
export function formatFileSize(sizeInBytes?: number): string {
  const sizeNumber = Number(sizeInBytes);

  if (sizeInBytes == null || Number.isNaN(sizeNumber)) return "";
  if (sizeNumber < 0) return "0 B";
  if (sizeNumber === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = sizeNumber;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Round to 2 decimal places, then format with locale-aware decimal separator
  const rounded = parseFloat(size.toFixed(2));
  const formatted = new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(rounded);

  return `${formatted} ${units[unitIndex]}`;
}
